import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setRefinancingCost,
  setCashOut,
  setMortagegAmount,
  setIntrestRate,
  setLoanTerm,
  setNewLoanTerm,
  setNewIntrestRate,
  setMortagePoints,
  setNewStartDate,
  setStartDate,
  setNewAmount,
  resetMortgageData,
  setCalculationResults,
} from "../redux/calculations/mortgageSlice";
import {
  resetChartData,
  updatedMortgageChart,
} from "../redux/calculations/calculaterSlice";
import CalculatorLayout from "../components/CalcLayout";
import Input from "../components/Input";
import { Button } from "../components/Button";
import { useFormik } from "formik";
import arrowImage from "../assets/svg/arrow.svg";
import * as Yup from "yup";
import useLocale from "../../src/locales/index";
import { logEvent } from "firebase/analytics";
import { analytics } from "../firebase";
import i18n from "../i18n";
import "react-datepicker/dist/react-datepicker.css";
import {
  enGB,
  enUS,
  hi,
  es,
  fr,
  ja,
  ar,
  ru,
  pt,
  id,
  faIR,
  tr,
  ko,
  de,
  it,
  uk,
  nl,
  zhCN,
  cs,
  da,
  fi,
  vi,
  th,
  ms,
} from "date-fns/locale";
import { registerLocale } from "react-datepicker";
import { useTranslation } from "react-i18next";
import MortgageDatePicker from "./Datepicker";
import DecimalUtils from "../utiles/decimalFormate";
import { Helmet } from "react-helmet";

registerLocale("en-GB", enGB);
registerLocale("en-US", enUS);
registerLocale("en-IN", enGB); // Using en-GB as fallback for en-IN
registerLocale("hi", hi);
registerLocale("es", es);
registerLocale("fr", fr);
registerLocale("ja", ja);
registerLocale("ar", ar);
registerLocale("ru", ru);
registerLocale("pt", pt);
registerLocale("id", id);
registerLocale("fa", faIR);
registerLocale("tr", tr);
registerLocale("ko", ko);
registerLocale("de", de);
registerLocale("it", it);
registerLocale("uk", uk);
registerLocale("nl", nl);
registerLocale("zh", zhCN);
registerLocale("cs", cs);
registerLocale("da", da);
registerLocale("fi", fi);
registerLocale("vi", vi);
registerLocale("th", th);
registerLocale("ms", ms);

const MortgageCalc = () => {
  const dispatch = useDispatch();
  const [calcDone, setCalcDone] = useState(false);
  const {
    mortgageAmount,
    intrestRate,
    loanTerm,
    newLoanTerm,
    newIntrestRate,
    mortgagePoints,
    refinancingCost,
    startDate,
    newStartDate,
    cashOut,
  } = useSelector((state) => state.mortgageCalculator);
  const { textData, timeUnits } = useLocale();
  const { mortgageCalc } = textData;
  const cAccept = localStorage.getItem("grow-cookies");
  const cookieAccept = JSON.parse(cAccept);
  const [loanTermUnit, setLoanTermUnit] = useState("Years");
  const [newLoanTermUnit, setNewLoanTermUnit] = useState("Years");
  const { t } = useTranslation();
  const validationSchema = Yup.object({
    startDate: Yup.date()
      .required(mortgageCalc.dueDateRequired)
      .typeError(mortgageCalc.invalidDate),

    newStartDate: Yup.date()
      .required(mortgageCalc.newDueDateRequired)
      .typeError(mortgageCalc.invalidDate)
      .min(
        Yup.ref("startDate"),
        mortgageCalc.newDueDateMustBeAfterOrEqualToDueDate
      ),

    mortgageAmount: Yup.number()
      .required(mortgageCalc.mortgageAmountRequired)
      .min(100000, mortgageCalc.mortgageAmount)
      .max(30000000, mortgageCalc.mortgageAmount),
    intrestRate: Yup.number()
      .required(mortgageCalc.interestRateRequired)
      .min(1, mortgageCalc.interestRate)
      .max(15, mortgageCalc.interestRate),
    loanTerm: Yup.number()
      .required(mortgageCalc.interestRateRequired)
      .min(
        loanTermUnit === "Years" ? 1 : loanTermUnit === "Months" ? 1 : 1,
        loanTermUnit === "Years"
          ? mortgageCalc.intrestRateYearsMin
          : loanTermUnit === "Months"
            ? mortgageCalc.intrestRateMonthsMin
            : mortgageCalc.intrestRateDaysMin
      )
      .max(
        loanTermUnit === "Years" ? 30 : loanTermUnit === "Months" ? 360 : 30,
        loanTermUnit === "Years"
          ? mortgageCalc.intrestRateYearsMax
          : loanTermUnit === "Months"
            ? mortgageCalc.intrestRateMonthsMax
            : mortgageCalc.intrestRateDaysMax
      ),
    newLoanTerm: Yup.number()
      .required(mortgageCalc.newLoanTermRequired)
      .min(
        newLoanTermUnit === "Years" ? 1 : newLoanTermUnit === "Months" ? 1 : 1,
        newLoanTermUnit === "Years"
          ? mortgageCalc.newLoanTermYearsMin
          : newLoanTermUnit === "Months"
            ? mortgageCalc.newLoanTermMonthsMin
            : mortgageCalc.newLoanTermDaysMin
      )
      .max(
        newLoanTermUnit === "Years"
          ? 30
          : newLoanTermUnit === "Months"
            ? 360
            : 30,
        newLoanTermUnit === "Years"
          ? mortgageCalc.newLoanTermYearsMax
          : newLoanTermUnit === "Months"
            ? mortgageCalc.newLoanTermMonthsMax
            : mortgageCalc.newLoanTermDaysMax
      ),
    newIntrestRate: Yup.number()
      .required(mortgageCalc.newInterestRateRequired)
      .min(1, mortgageCalc.newInterestRate)
      .max(15, mortgageCalc.newInterestRate),
    mortgagePoints: Yup.number()
      .required(mortgageCalc.mortgagePointsRequired)
      .min(0, mortgageCalc.mortgagePoints)
      .max(10, mortgageCalc.mortgagePoints),
    refinancingCost: Yup.number()
      .required(mortgageCalc.refinancingCostRequired)
      .min(0, mortgageCalc.refinancingCost)
      .max(200000, mortgageCalc.refinancingCost),
    cashOut: Yup.number()
      .required(mortgageCalc.cashOutRequired)
      .min(0, mortgageCalc.cashOut)
      .max(500000, mortgageCalc.cashOut),
  });
  const getMonthsElapsed = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    const yearDiff = end.getFullYear() - start.getFullYear();
    const monthDiff = end.getMonth() - start.getMonth();

    return yearDiff * 12 + monthDiff;
  };

  const onSubmit = (values) => {
    if (cookieAccept && analytics) {
      logEvent(analytics, "calculate_MORTGAGE");
    }
    try {
      const principal = Number(values.mortgageAmount);

      // Convert loan terms to months based on the selected unit
      const termMonths =
        loanTermUnit === "Years"
          ? Number(values.loanTerm) * 12
          : Number(values.loanTerm);

      const newTerm =
        newLoanTermUnit === "Years"
          ? Number(values.newLoanTerm) * 12
          : Number(values.newLoanTerm);

      const annualRate = Number(values.intrestRate);
      const newRate = Number(values.newIntrestRate);
      const pointsPercent = Number(values.mortgagePoints);
      const refinanceCost = Number(values.refinancingCost);
      const cashAdjustment = Number(values.cashOut);
      const startDate = values.startDate
        ? new Date(values.startDate).getTime()
        : null;
      const newStart = values.newStartDate
        ? new Date(values.newStartDate).getTime()
        : null;

      const inputs = [
        principal,
        startDate,
        termMonths,
        annualRate,
        newStart,
        newTerm,
        newRate,
        pointsPercent,
        refinanceCost,
        cashAdjustment,
      ];

      if (
        inputs.some(isNaN) ||
        inputs.some((val) => val < 0) ||
        newStart <= startDate
      ) {
        throw new Error(
          "Invalid input: Ensure all values are valid, positive, and newStartDate is after originalStartDate."
        );
      }

      const monthsElapsed = getMonthsElapsed(startDate, newStart);

      const monthlyRate = annualRate / 12 / 100;
      const newMonthlyRate = newRate / 12 / 100;

      const calculateMonthlyPayment = (amount, rate, months) => {
        return rate === 0
          ? amount / months
          : (amount * (rate * Math.pow(1 + rate, months))) /
          (Math.pow(1 + rate, months) - 1);
      };

      const originalMonthlyPayment = calculateMonthlyPayment(
        principal,
        monthlyRate,
        termMonths
      );

      // Remaining balance on current loan
      let remainingBalance;
      if (monthlyRate === 0) {
        remainingBalance = principal - originalMonthlyPayment * monthsElapsed;
      } else {
        remainingBalance =
          principal * Math.pow(1 + monthlyRate, monthsElapsed) -
          originalMonthlyPayment *
          ((Math.pow(1 + monthlyRate, monthsElapsed) - 1) / monthlyRate);
      }

      // New loan calculations
      const mortgagePointsCost = (pointsPercent / 100) * remainingBalance;
      const newLoanAmount =
        remainingBalance + refinanceCost + mortgagePointsCost - cashAdjustment;
      const newMonthlyPayment = calculateMonthlyPayment(
        newLoanAmount,
        newMonthlyRate,
        newTerm
      );

      // Monthly savings
      const monthlySavings = originalMonthlyPayment - newMonthlyPayment;
      const totalRefinanceCost = refinanceCost + mortgagePointsCost;
      const breakEvenMonths =
        monthlySavings <= 0
          ? Infinity
          : Math.ceil(totalRefinanceCost / monthlySavings);

      // Calculate results
      const results = {
        remainingBalance: Math.round(remainingBalance * 100) / 100,
        newLoanAmount: Math.round(newLoanAmount * 100) / 100,
        newMonthlyPayment: Math.round(newMonthlyPayment * 100) / 100,
        originalMonthlyPayment: Math.round(originalMonthlyPayment * 100) / 100,
        monthlySavings: Math.round(monthlySavings * 100) / 100,
        breckEvenMonth: Math.round(breakEvenMonths),
        refinanceCost: Math.round(totalRefinanceCost * 100) / 100,
        pointsCost: Math.round(mortgagePointsCost * 100) / 100,
        inputs: {
          elapsedMonths: monthsElapsed,
          pointsCost: Math.round(mortgagePointsCost * 100) / 100,
          cashInOut: cashAdjustment,
        },
      };
      dispatch(setNewAmount(newMonthlyPayment));
      dispatch(setCalculationResults(results));

      // Dispatch the results to update the mortgage chart
      dispatch(
        updatedMortgageChart({
          remainingBalance: Math.round(remainingBalance * 100) / 100,
          newLoanAmount: Math.round(newLoanAmount * 100) / 100,
          monthlyPayment: Math.round(originalMonthlyPayment * 100) / 100,
          newMonthlyPayment: Math.round(newMonthlyPayment * 100) / 100,
          monthlySaving: Math.round(monthlySavings * 100) / 100,
          refinanceCost: Math.round(totalRefinanceCost * 100) / 100,
          breakEvenMonths: isFinite(breakEvenMonths) ? breakEvenMonths : null,
          monthsElapsed: Math.round(monthsElapsed * 100) / 100,
          mortgagePointsCost: Math.round(mortgagePointsCost * 100) / 100,
          cashAdjustment,
        })
      );

      setCalcDone(true);
      return results;
    } catch (error) {
      if (error instanceof Error) {
        console.error("Error in mortgage calculation:", error.message);
      } else {
        console.error("An unexpected error occurred:", error);
      }
      return undefined;
    }
  };

  const handleLoanTermSliderChange = (e) => {
    const value = parseFloat(e.target.value);
    dispatch(setLoanTerm(value));
    formik.setFieldValue("loanTerm", value);
    setCalcDone(false);
  };

  // For newLoanTerm slider
  const handleNewLoanTermSliderChange = (e) => {
    const value = parseFloat(e.target.value);
    dispatch(setNewLoanTerm(value));
    formik.setFieldValue("newLoanTerm", value);
    setCalcDone(false);
  };

  useEffect(() => {
    // Initialize the form with values from Redux
    formik.setValues({
      ...formik.values,
      mortgageAmount,
      intrestRate,
      loanTerm,
      newLoanTerm,
      newIntrestRate,
      mortgagePoints,
      refinancingCost,
      startDate,
      newStartDate,
      cashOut,
    });
  }, []);
  useEffect(() => {
    return () => {
      dispatch(resetChartData());
      dispatch(resetMortgageData());
    };
  }, [dispatch]);
  const formik = useFormik({
    initialValues: {
      mortgageAmount: mortgageAmount,
      intrestRate: intrestRate,
      loanTerm: loanTerm,
      newLoanTerm: newLoanTerm,
      newIntrestRate: newIntrestRate,
      mortgagePoints: mortgagePoints,
      refinancingCost: refinancingCost,
      cashOut: cashOut,
      startDate: startDate || "",
      newStartDate: newStartDate || "",
    },
    validationSchema: validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: onSubmit,
  });

  const formatToINR = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    if (value === 0) return "0";

    const shouldForceIndianFormat = i18n.language === "fa";

    return isNaN(Number(value))
      ? ""
      : Number(value).toLocaleString(
        shouldForceIndianFormat ? "en-IN" : i18n.language || "en-IN"
      );
  };

  const mortgageAmountRef = useRef(null);
  const intrestSliderRef = useRef(null);
  const termSliderRef = useRef(null);
  const newIntrestRef = useRef(null);
  const newTermref = useRef(null);
  const refinanceCostSliderRef = useRef(null);
  const pointSliderRef = useRef(null);
  const cashOutRef = useRef(null);

  const handleMortgageSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setMortagegAmount(value));
    formik.setFieldValue("mortgageAmount", value);
    setCalcDone(false);
  };

  const handleInterestSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setIntrestRate(value));
    formik.setFieldValue("intrestRate", value);
    setCalcDone(false);
  };

  const handleNewIntrestrateChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue === "") {
      formik.setFieldValue("newIntrestRate", rawValue);
      dispatch(setNewIntrestRate(""));
      return;
    }
    if (rawValue.length > 5) return;
    if (!DecimalUtils.getValidationPattern().test(rawValue)) return;
    const normalizedValue = DecimalUtils.normalizeInput(rawValue);

    formik.setFieldValue("newIntrestRate", normalizedValue);
    dispatch(setNewIntrestRate(normalizedValue));
    setCalcDone(false);
  };

  const handleMortgagePointsChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue === "") {
      formik.setFieldValue("mortgagePoints", rawValue);
      dispatch(setMortagePoints(""));
      return;
    }
    if (rawValue.length > 5) return;
    if (!DecimalUtils.getValidationPattern().test(rawValue)) return;
    const normalizedValue = DecimalUtils.normalizeInput(rawValue);

    formik.setFieldValue("mortgagePoints", normalizedValue);
    dispatch(setMortagePoints(normalizedValue));
    setCalcDone(false);
  };

  const handleIntrestrateChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue === "") {
      formik.setFieldValue("intrestRate", rawValue);
      dispatch(setIntrestRate(""));
      return;
    }
    if (rawValue.length > 5) return;
    if (!DecimalUtils.getValidationPattern().test(rawValue)) return;
    const normalizedValue = DecimalUtils.normalizeInput(rawValue);

    formik.setFieldValue("intrestRate", normalizedValue);
    dispatch(setIntrestRate(normalizedValue));
    setCalcDone(false);
  };

  const toggleLoanTermUnit = () => {
    if (loanTermUnit === "Years") {
      setLoanTermUnit("Months");
      const monthsValue = formik.values.loanTerm * 12;
      dispatch(setLoanTerm(monthsValue));
      formik.setFieldValue("loanTerm", monthsValue);
    } else {
      setLoanTermUnit("Years");
      const yearsValue = formik.values.loanTerm / 12;
      dispatch(setLoanTerm(yearsValue));
      formik.setFieldValue("loanTerm", yearsValue);
    }
    setCalcDone(false);
  };

  const toggleNewLoanTermUnit = () => {
    if (newLoanTermUnit === "Years") {
      setNewLoanTermUnit("Months");
      const monthsValue = formik.values.newLoanTerm * 12;
      dispatch(setNewLoanTerm(monthsValue));
      formik.setFieldValue("newLoanTerm", monthsValue);
    } else {
      setNewLoanTermUnit("Years");
      const yearsValue = formik.values.newLoanTerm / 12;
      dispatch(setNewLoanTerm(yearsValue));
      formik.setFieldValue("newLoanTerm", yearsValue);
    }
    setCalcDone(false);
  };

  const handleNewInterestSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setNewIntrestRate(value));
    formik.setFieldValue("newIntrestRate", value);
    setCalcDone(false);
  };

  const handleRefinanceSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setRefinancingCost(value));
    formik.setFieldValue("refinancingCost", value);
    setCalcDone(false);
  };

  const handlePointsSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setMortagePoints(value));
    formik.setFieldValue("mortgagePoints", value);
    setCalcDone(false);
  };

  const handleCashoutSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setCashOut(value));
    formik.setFieldValue("cashOut", value);
    setCalcDone(false);
  };

  const handleMortgageChange = (e) => {
    const rawValue = e.target.value.replace(/,/g, "");
    if (rawValue.length > 10) return;

    if (rawValue === "") {
      formik.setFieldValue("mortgageAmount", "");
      dispatch(setMortagegAmount(""));
      return;
    }
    const numericValue = parseFloat(
      rawValue
        .replace(/,/g, ".")
        .replace(/\./g, "")
        .replace(/(\d+)(\.\d{2})?/, "$1.$2")
    ); // Convert to a number

    formik.setFieldValue("mortgageAmount", numericValue);

    dispatch(setMortagegAmount(numericValue));
    setCalcDone(false);
  };

  useEffect(() => {
    dispatch(setRefinancingCost(formik.values.refinancingCost));
    dispatch(setCashOut(formik.values.cashOut));
    dispatch(setMortagegAmount(formik.values.mortgageAmount));
    dispatch(setIntrestRate(formik.values.intrestRate));
    dispatch(setLoanTerm(formik.values.loanTerm));
    dispatch(setNewLoanTerm(formik.values.newLoanTerm));
    dispatch(setNewIntrestRate(formik.values.newIntrestRate));
    dispatch(setMortagePoints(formik.values.mortgagePoints));
  }, [formik.values, dispatch]);

  // Add new handlers for formatted input
  const handleRefinancingCostChange = (e) => {
    const rawValue = e.target.value.replace(/,/g, "");
    if (rawValue.length > 6) return;

    if (rawValue === "") {
      formik.setFieldValue("refinancingCost", "");
      dispatch(setRefinancingCost(""));
      return;
    }
    const numericValue = parseFloat(
      rawValue
        .replace(/,/g, ".")
        .replace(/\./g, "")
        .replace(/(\d+)(\.\d{2})?/, "$1.$2")
    ); // Convert to a number
    formik.setFieldValue("refinancingCost", numericValue);
    dispatch(setRefinancingCost(numericValue));
    setCalcDone(false);
  };

  const handleCashOutChange = (e) => {
    const rawValue = e.target.value.replace(/,/g, "");
    if (rawValue.length > 6) return;

    if (rawValue === "") {
      formik.setFieldValue("cashOut", "");
      dispatch(setCashOut(""));
      return;
    }
    const numericValue = parseFloat(
      rawValue
        .replace(/,/g, ".")
        .replace(/\./g, "")
        .replace(/(\d+)(\.\d{2})?/, "$1.$2")
    ); // Convert to a number
    formik.setFieldValue("cashOut", numericValue);
    dispatch(setCashOut(numericValue));
    setCalcDone(false);
  };
  const isRTL = () => {
    const rtlLanguages = ["ar", "fa", "ur"];
    return rtlLanguages.includes(i18n.language);
  };

  const getFormattedPlaceholder = (language) => {
    const baseValue = 10000;
    const formatLang = language === "fa" ? "en-IN" : language;
    return new Intl.NumberFormat(formatLang).format(baseValue);
  };
  const descriptionContent = (
    <div className="p-4 sm:p-5 w-full bg-[#F6F8FB] rounded-xl">
      <h2 className="text-black text-base sm:text-xl font-medium">
        {" "}
        {textData.morgageContent.title}
      </h2>
      <p className="leading-relaxed mt-2  text-sm sm:text-[15px]">
        {textData.morgageContent.description}
      </p>

      <h2 className="text-black text-base sm:text-xl font-medium pt-3">
        {" "}
        {textData.morgageContent.howItWorksTitle}
      </h2>
      <p className="leading-relaxed mt-2  text-sm sm:text-[15px]">
        {textData.morgageContent.howItWorksDescription}
      </p>

      <p className="font-medium bg-[#E0E4F0] text-sm sm:text-base p-3 mt-3">
        <strong>
          M = P x [r(1 + r)<sup>n</sup>] / [(1 + r)<sup>n</sup> - 1]
        </strong>
      </p>
      <table className="border-collapse mt-4 w-full">
        <thead>
          <tr className="bg-gray-100">
            <th className="p-3 border border-gray-light w-1/3 text-left text-white bg-[#3498db] font-semibold">
              {textData.morgageContent.symbolColumn}
            </th>
            <th className="p-3 border border-gray-light w-2/3 text-left text-white  bg-[#3498db] font-semibold">
              {textData.morgageContent.descriptionColumn}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              M
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.morgageContent.formulaSymbols[0].description}
            </td>
          </tr>
          <tr className="bg-transparent">
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              P
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.morgageContent.formulaSymbols[1].description}
            </td>
          </tr>
          <tr className="bg-transparent">
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              r
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.morgageContent.formulaSymbols[2].description}
            </td>
          </tr>
          <tr className="bg-transparent">
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              n
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.morgageContent.formulaSymbols[3].description}
            </td>
          </tr>
        </tbody>
      </table>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {" "}
        {textData.morgageContent.howToUseMortgageCalculatorTitle}
      </h2>
      <ol className="list-decimal px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>
          <strong>
            {" "}
            {textData.morgageContent.howToUseMortgageCalculatorSteps[0].detail}
          </strong>
          {
            textData.morgageContent.howToUseMortgageCalculatorSteps[0]
              .description
          }
        </li>
        <li>
          <strong>
            {" "}
            {textData.morgageContent.howToUseMortgageCalculatorSteps[1].detail}
          </strong>
          {
            textData.morgageContent.howToUseMortgageCalculatorSteps[1]
              .description
          }
        </li>
        <li>
          <strong>
            {" "}
            {textData.morgageContent.howToUseMortgageCalculatorSteps[2].detail}
          </strong>
          {
            textData.morgageContent.howToUseMortgageCalculatorSteps[2]
              .description
          }
        </li>
        <li>
          <strong>
            {" "}
            {textData.morgageContent.howToUseMortgageCalculatorSteps[3].detail}
          </strong>
          {
            textData.morgageContent.howToUseMortgageCalculatorSteps[3]
              .description
          }
        </li>
      </ol>
      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {" "}
        {textData.morgageContent.benefitsOfMortgageCalculatorTitle}
      </h2>
      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>
          <strong>
            {textData.morgageContent.benefitsOfMortgageCalculator[0].title}
          </strong>{" "}
          {textData.morgageContent.benefitsOfMortgageCalculator[0].description}
        </li>
        <li>
          <strong>
            {textData.morgageContent.benefitsOfMortgageCalculator[1].title}
          </strong>{" "}
          {textData.morgageContent.benefitsOfMortgageCalculator[1].description}
        </li>
        <li>
          <strong>
            {textData.morgageContent.benefitsOfMortgageCalculator[2].title}
          </strong>{" "}
          {textData.morgageContent.benefitsOfMortgageCalculator[2].description}
        </li>
        <li>
          <strong>
            {textData.morgageContent.benefitsOfMortgageCalculator[3].title}
          </strong>{" "}
          {textData.morgageContent.benefitsOfMortgageCalculator[3].description}
        </li>
        <li>
          <strong>
            {textData.morgageContent.benefitsOfMortgageCalculator[4].title}
          </strong>{" "}
          {textData.morgageContent.benefitsOfMortgageCalculator[4].description}
        </li>
      </ul>
      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {" "}
        {textData.morgageContent.keyConsiderationsMortgageTitle}
      </h2>
      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>
          <strong>
            {" "}
            {textData.morgageContent.keyConsiderationsMortgage[0].factor}
          </strong>{" "}
          {textData.morgageContent.keyConsiderationsMortgage[0].description}
        </li>
        <li>
          <strong>
            {" "}
            {textData.morgageContent.keyConsiderationsMortgage[1].factor}
          </strong>{" "}
          {textData.morgageContent.keyConsiderationsMortgage[1].description}
        </li>
        <li>
          <strong>
            {" "}
            {textData.morgageContent.keyConsiderationsMortgage[2].factor}
          </strong>{" "}
          {textData.morgageContent.keyConsiderationsMortgage[2].description}
        </li>
        <li>
          <strong>
            {" "}
            {textData.morgageContent.keyConsiderationsMortgage[3].factor}
          </strong>{" "}
          {textData.morgageContent.keyConsiderationsMortgage[3].description}
        </li>
        <li>
          <strong>
            {" "}
            {textData.morgageContent.keyConsiderationsMortgage[4].factor}
          </strong>{" "}
          {textData.morgageContent.keyConsiderationsMortgage[4].description}
        </li>
      </ul>
    </div>
  );

  const timeLabel =
    loanTermUnit === "Years"
      ? t("yearShort")
      : loanTermUnit === "Months"
        ? t("month").charAt(0).toUpperCase()
        : t("day").charAt(0).toUpperCase();

  const timeNewLabel =
    newLoanTermUnit === "Years"
      ? t("yearShort")
      : newLoanTermUnit === "Months"
        ? t("month").charAt(0).toUpperCase()
        : t("day").charAt(0).toUpperCase();

  const timedayLabel = t("day").charAt(0).toUpperCase();

  return (
    <>
      <Helmet>
        <title>Mortgage Refinancing Calculator – Save on Your Home Loan</title>
        <meta
          name="description"
          content="Free Mortgage Refinancing Calculator Online to quickly calculate savings and plan finances refinancing decisions confidently and save on your home loan."
        />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://growlifefund.com/calculator/mortgage-loan-refinance-calculator"
        />

        {/* Open Graph */}
        <meta
          property="og:title"
          content="Mortgage Refinancing Calculator – Save on Your Home Loan"
        />
        <meta
          property="og:description"
          content="Free Mortgage Refinancing Calculator Online to quickly calculate savings and plan finances refinancing decisions confidently and save on your home loan."
        />
        <meta
          property="og:url"
          content="https://growlifefund.com/calculator/mortgage-loan-refinance-calculator"
        />
        <meta property="og:site_name" content="growlifefund" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en" />
        <meta
          property="og:image:alt"
          content="Mortgage Refinancing Calculator Online - GrowLifeFund"
        />

        {/* Robots */}
        <meta name="robots" content="index, follow" />
      </Helmet>
      <CalculatorLayout
        calcDone={calcDone}
        title={
          <>
            {textData.mortgageCalc.title}{" "}
            <span className="text-black lg:text-xl sm:text-lg text-lg">
              {textData.savingsTitle}{" "}
            </span>
          </>
        }
        description={textData.mortgageCalc.description}
        descriptionContent={descriptionContent}
      >
        <div className="">
          <form onSubmit={formik.handleSubmit} className="flex flex-col gap-6">
            <h2 className="font-bold text-base text-black pb-4 sm:pb-4 text-start sm:text-[16px]">
              {textData.mortgageCalc.currentMortgage}
            </h2>

            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.mortgageCalc.originalMortgageAmountLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={mortgageAmountRef}>

                  <input
                    type="range"
                    min={100000}
                    max={30000000}
                    step={100000}
                    value={mortgageAmount}
                    onChange={handleMortgageSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((mortgageAmount - 100000) / (30000000 - 100000)) * 100
                        }%`,
                    }}
                  />
                </div>
                <div className="flex items-center flex-col">
                  <Input
                    name="mortgageAmount"
                    leftIcon={textData.currency}
                    value={formatToINR(formik.values.mortgageAmount)}
                    onChange={handleMortgageChange}
                    onBlur={formik.handleBlur}
                    type="text"
                    dir="ltr"
                    placeholder={getFormattedPlaceholder(10000, i18n.language)}
                    borderColor={
                      formik.touched.mortgageAmount &&
                        formik.errors.mortgageAmount
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={`flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px] border ${formik.touched.mortgageAmount &&
                        formik.errors.mortgageAmount
                        ? "border-danger"
                        : "border-gray-light"
                      } `}
                  />
                  {formik.touched.mortgageAmount &&
                    formik.errors.mortgageAmount && (
                      <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                        {formik.errors.mortgageAmount}
                      </div>
                    )}
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.mortgageCalc.dueDate}
                </label>
                <div className="flex items-center flex-col">
                  <MortgageDatePicker
                    value={formik.values.startDate}
                    leftIcon={timedayLabel}
                    onDateChange={(date) =>
                      formik.setFieldValue("startDate", date)
                    }
                    name="startDate"
                    placeholder="DD/MM/YYYY"
                    className="flex items-center py-[7.5px] !border-gray-light !rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]"
                  />
                  {formik.touched.startDate && formik.errors.startDate && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.startDate}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.mortgageCalc.interestRateLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={intrestSliderRef}>


                  <input
                    type="range"
                    min={1}
                    max={15}
                    step={0.1}
                    value={parseFloat(intrestRate)}
                    onChange={handleInterestSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((intrestRate - 1) / (15 - 1)) * 100}%`,
                    }}
                  />
                </div>
                <div className="flex items-center flex-col">
                  <Input
                    name="intrestRate"
                    leftIcon="%"
                    type="text"
                    placeholder={DecimalUtils.getPlaceholder("6.5")}
                    value={DecimalUtils.formatDisplay(
                      formik.values.intrestRate
                    )}
                    onChange={handleIntrestrateChange}
                    onBlur={formik.handleBlur}
                    borderColor={
                      formik.touched.intrestRate && formik.errors.intrestRate
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={`flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px] border ${formik.touched.intrestRate && formik.errors.intrestRate
                        ? "border-danger"
                        : "border-gray-light"
                      } `}
                  />
                  {formik.touched.intrestRate && formik.errors.intrestRate && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.intrestRate}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm flex gap-1 sm:gap-2 flex-row">
                  {textData.mortgageCalc.mortgageTermLabel}

                  <div
                    onClick={toggleLoanTermUnit}
                    className=" flex items-center cursor-pointer"
                  >
                    <div className="relative   w-full flex items-center gap-[2px]">
                      <div className="text-primary text-sm sm:text-sm font-normal">
                        {timeUnits[loanTermUnit]}
                      </div>
                      <div className="absolute -bottom-1 left-0 right-0 h-0.5 "></div>
                      <img
                        src={arrowImage}
                        alt="Dropdown arrow"
                        className="w-3 h-3 sm:w-4 sm:h-4"
                      />
                    </div>
                  </div>
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={termSliderRef}>

                  <input
                    type="range"
                    min={loanTermUnit === "Years" ? 1 : 1}
                    max={loanTermUnit === "Years" ? 30 : 360}
                    step={1}
                    value={formik.values.loanTerm}
                    onChange={handleLoanTermSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((formik.values.loanTerm -
                          (loanTermUnit === "Years" ? 1 : 1)) /
                          ((loanTermUnit === "Years" ? 30 : 360) -
                            (loanTermUnit === "Years" ? 1 : 1))) *
                        100
                        }%`,
                    }}
                  />
                </div>
                <div className="flex items-center flex-col">
                  <Input
                    name="loanTerm"
                    leftIcon={timeLabel}
                    type="number"
                    value={formik.values.loanTerm}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d{0,3}$/.test(value)) {
                        formik.handleChange(e);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    placeholder={
                      loanTermUnit === "Years"
                        ? "5"
                        : loanTermUnit === "Months"
                          ? "60"
                          : "5"
                    }
                    borderColor={
                      formik.touched.loanTerm && formik.errors.loanTerm
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={`flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px] border ${formik.touched.loanTerm && formik.errors.loanTerm
                        ? "border-danger"
                        : "border-gray-light"
                      } `}
                  />
                  {formik.touched.loanTerm && formik.errors.loanTerm && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.loanTerm}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <h2 className="font-bold text-base text-black pb-4 sm:pb-4 text-start sm:text-[16px]">
              {textData.mortgageCalc.mortgageRefinance}
            </h2>

            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.mortgageCalc.newDueDateLabel}
                </label>
                <div className="flex items-center flex-col">
                  <MortgageDatePicker
                    value={formik.values.newStartDate}
                    leftIcon={timedayLabel}
                    onDateChange={(date) =>
                      formik.setFieldValue("newStartDate", date)
                    }
                    name="newStartDate"
                    placeholder="DD/MM/YYYY"
                    className="flex items-center py-[7.5px] border !border-gray-light !rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]"
                  />
                  {formik.touched.newStartDate &&
                    formik.errors.newStartDate && (
                      <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                        {formik.errors.newStartDate}
                      </div>
                    )}
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.mortgageCalc.newInterestRateLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={newIntrestRef}>

                  <input
                    type="range"
                    min={1}
                    max={15}
                    step={0.1}
                    value={parseFloat(newIntrestRate)}
                    onChange={handleNewInterestSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((newIntrestRate - 1) / (15 - 1)) * 100}%`,
                    }}
                  />
                </div>{" "}
                <div className="flex items-center flex-col">
                  <Input
                    name="newIntrestRate"
                    leftIcon="%"
                    type="text"
                    placeholder={DecimalUtils.getPlaceholder("6.5")}
                    value={DecimalUtils.formatDisplay(
                      formik.values.newIntrestRate
                    )}
                    onChange={handleNewIntrestrateChange}
                    onBlur={formik.handleBlur}
                    borderColor={
                      formik.touched.newIntrestRate &&
                        formik.errors.newIntrestRate
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={`flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px] border ${formik.touched.newIntrestRate &&
                        formik.errors.newIntrestRate
                        ? "border-danger"
                        : "border-gray-light"
                      }`}
                  />
                  {formik.touched.newIntrestRate &&
                    formik.errors.newIntrestRate && (
                      <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                        {formik.errors.newIntrestRate}
                      </div>
                    )}
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm flex gap-1 sm:gap-2 flex-row">
                  {textData.mortgageCalc.newLoanTermLabel}

                  <div
                    onClick={toggleNewLoanTermUnit}
                    className=" flex items-center cursor-pointer"
                  >
                    <div className="relative   w-full flex items-center gap-[2px]">
                      <div className="text-primary text-sm sm:text-sm font-normal">
                        {timeUnits[newLoanTermUnit]}
                      </div>
                      <div className="absolute -bottom-1 left-0 right-0 h-0.5 "></div>
                      <img
                        src={arrowImage}
                        alt="Dropdown arrow"
                        className="w-3 h-3 sm:w-4 sm:h-4"
                      />
                    </div>
                  </div>
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={newTermref}>

                  <input
                    type="range"
                    min={newLoanTermUnit === "Years" ? 1 : 1}
                    max={newLoanTermUnit === "Years" ? 30 : 360}
                    step={1}
                    value={newLoanTerm}
                    onChange={handleNewLoanTermSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((formik.values.newLoanTerm -
                          (newLoanTermUnit === "Years" ? 1 : 1)) /
                          ((newLoanTermUnit === "Years" ? 30 : 360) -
                            (newLoanTermUnit === "Years" ? 1 : 1))) *
                        100
                        }%`,
                    }}
                  />
                </div>
                <div className="flex items-center flex-col">
                  <Input
                    name="newLoanTerm"
                    leftIcon={timeNewLabel}
                    type="number"
                    value={formik.values.newLoanTerm}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d{0,3}$/.test(value)) {
                        formik.handleChange(e);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    placeholder={
                      newLoanTermUnit === "Years"
                        ? "5"
                        : newLoanTermUnit === "Months"
                          ? "60"
                          : "5"
                    }
                    borderColor={
                      formik.touched.newLoanTerm && formik.errors.newLoanTerm
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={`flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px] border ${formik.touched.newLoanTerm && formik.errors.newLoanTerm
                        ? "border-danger"
                        : "border-gray-light"
                      } `}
                  />
                  {formik.touched.newLoanTerm && formik.errors.newLoanTerm && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.newLoanTerm}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.mortgageCalc.refinancingCostsLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div
                  className="relative   w-full "
                  ref={refinanceCostSliderRef}
                >

                  <input
                    type="range"
                    min={0}
                    max={200000}
                    step={500}
                    value={refinancingCost}
                    onChange={handleRefinanceSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((refinancingCost - 0) / (200000 - 0)) * 100
                        }%`,
                    }}
                  />
                </div>{" "}
                <div className="flex items-center flex-col">
                  <Input
                    name="refinancingCost"
                    leftIcon={textData.currency}
                    type="text"
                    dir="ltr"
                    placeholder={getFormattedPlaceholder(1500, i18n.language)}
                    value={formatToINR(formik.values.refinancingCost)}
                    onChange={handleRefinancingCostChange}
                    onBlur={formik.handleBlur}
                    borderColor={
                      formik.touched.refinancingCost &&
                        formik.errors.refinancingCost
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={`flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px] border ${formik.touched.refinancingCost &&
                        formik.errors.refinancingCost
                        ? "border-danger"
                        : "border-gray-light"
                      } `}
                  />
                  {formik.touched.refinancingCost &&
                    formik.errors.refinancingCost && (
                      <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                        {formik.errors.refinancingCost}
                      </div>
                    )}
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.mortgageCalc.mortgagePointsLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={pointSliderRef}>

                  <input
                    type="range"
                    min={0}
                    max={10}
                    step={0.1}
                    value={parseFloat(mortgagePoints)}
                    onChange={handlePointsSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((mortgagePoints - 0) / (10 - 0)) * 100}%`,
                    }}
                  />
                </div>{" "}
                <div className="flex items-center flex-col">
                  <Input
                    name="mortgagePoints"
                    leftIcon="%"
                    type="text"
                    placeholder={DecimalUtils.getPlaceholder("6.5")}
                    value={DecimalUtils.formatDisplay(
                      formik.values.mortgagePoints
                    )}
                    onChange={handleMortgagePointsChange}
                    onBlur={formik.handleBlur}
                    borderColor={
                      formik.touched.mortgagePoints &&
                        formik.errors.mortgagePoints
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={`flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px] border ${formik.touched.mortgagePoints &&
                        formik.errors.mortgagePoints
                        ? "border-danger"
                        : "border-gray-light"
                      }`}
                  />
                  {formik.touched.mortgagePoints &&
                    formik.errors.mortgagePoints && (
                      <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                        {formik.errors.mortgagePoints}
                      </div>
                    )}
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.mortgageCalc.cashInOutLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={cashOutRef}>

                  <input
                    type="range"
                    min={0}
                    max={500000}
                    step={500}
                    value={cashOut}
                    onChange={handleCashoutSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((cashOut - 0) / (500000 - 0)) * 100}%`,
                    }}
                  />
                </div>
                <div className="flex items-center flex-col">
                  <Input
                    name="cashOut"
                    leftIcon={textData.currency}
                    type="text"
                    dir="ltr"
                    placeholder={getFormattedPlaceholder(2500, i18n.language)}
                    value={formatToINR(formik.values.cashOut)}
                    onChange={handleCashOutChange}
                    onBlur={formik.handleBlur}
                    borderColor={
                      formik.touched.cashOut && formik.errors.cashOut
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={`flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px] border ${formik.touched.cashOut && formik.errors.cashOut
                        ? "border-danger"
                        : "border-gray-light"
                      }`}
                  />
                  {formik.touched.cashOut && formik.errors.cashOut && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.cashOut}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-[10px]">
              <Button type="submit" className="w-full">
                {textData.calculate}
              </Button>
            </div>
          </form>
        </div>
      </CalculatorLayout>
    </>
  );
};

export default MortgageCalc;
